<template>
  <div class="editable-text">
    <div
      class="read-view"
      v-if="!isEditing"
      @click="startEditing"
      :class="{ 'single-line': !multiline }"
      :style="{ minWidth, maxWidth }"
    >
      <span class="placeholder" v-if="!hasText()">{{ placeholder || '&nbsp;' }}</span>
      <span v-if="hasText()">{{ text }}</span>
    </div>
    <div class="write-view" v-if="isEditing" v-click-away="finishEditing">
      <textarea-autosize
        v-if="multiline"
        ref="textfield"
        v-model="text"
        @keydown.enter.native="finishEditing"
        rows="1"
        :placeholder="placeholder"
      />
      <span
        class="edit-single-line"
        ref="textfield"
        :style="{ minWidth, maxWidth }"
        v-if="!multiline"
        @keydown.enter="finishEditing"
        @input="onInput"
        contenteditable="true"
        :data-placeholder="getPlaceholder()"
        >{{ value }}</span
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick } from 'vue';
import HtmlFormatter from '@/shared/services/htmlFormatter';

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  multiline: {
    type: Boolean,
    default: false,
  },
  allowEmpty: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: String,
  },
  minWidth: {
    type: String,
  },
  maxChars: {
    type: Number,
    default: 100,
  },
});

const emit = defineEmits<{
  (e: 'text-updated', value: string, oldValue: string): void;
}>();

const textfield = ref<any>(null);

const text = ref('');
const isEditing = ref(false);

watch(
  () => props.value,
  (value: string) => (text.value = value),
  { immediate: true },
);

function hasText() {
  return text.value && text.value.length > 0;
}

function getPlaceholder() {
  return props.placeholder ? props.placeholder : '&nbsp;';
}

function startEditing() {
  isEditing.value = true;
  nextTick(() => {
    let el = textfield.value;
    if (props.multiline) {
      el = textfield.value.$el;
    }
    HtmlFormatter.setCaretAtTheEndOfContentEditable(el);
  });
}

function finishEditing() {
  isEditing.value = false;
  const previousValue = props.value;
  if (!props.allowEmpty && textEmpty()) {
    text.value = props.value;
    emit('text-updated', text.value, previousValue);
  } else {
    emit('text-updated', text.value, previousValue);
  }
}

function onInput(e: any) {
  const userInput = e.target.innerText;
  if (userInput.length < props.maxChars) {
    text.value = userInput;
  }
}

function textEmpty(): boolean {
  return typeof text.value === 'undefined' || !text.value || 0 === text.value.length;
}
</script>

<style scoped lang="scss">
span {
  cursor: auto;
}
.placeholder {
  opacity: 0.6;
}

[contenteditable='true']:empty:not(:focus):before {
  content: attr(data-placeholder);
  color: #7d7d7d;
}

.edit-single-line {
  display: block;
  white-space: nowrap;
  overflow: hidden;
}

.read-view {
  border: 1px solid transparent;
  box-shadow:
    inset 0 1px 2px transparent,
    0 0 5px transparent;
  border-radius: 4px;
  padding-left: 4px;
  &.single-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.write-view {
  line-height: inherit;
  input[type='text'],
  textarea,
  .edit-single-line {
    width: 100%;
    padding-left: 4px;
    border: none;
    background-color: transparent;
    font-size: inherit;
    color: inherit;
  }

  input[type='text']:focus,
  textarea:focus,
  .edit-single-line {
    outline: none;
    border: 1px solid #3273dc;
    box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
    border-radius: 4px;
  }
}
</style>
